/**
 * Pure ring math for the `intersects_polygon` spatial filter (T-draw). No engine, no maplibre, no
 * React — unit-testable and eager-safe. Owns the ONE canonical form every emit + the session
 * coercer share: an OPEN ring (first vertex NOT repeated), consecutive/cyclic duplicates removed,
 * longitudes wrapped into [-180,180], wound COUNTER-CLOCKWISE.
 *
 * Winding: CCW is harmless for planar PostGIS geometry + MySQL (ST_Intersects is orientation-
 * agnostic there) and REQUIRED for SQL Server geography (left-hand rule) and Oracle exterior rings.
 * So a single CCW canonicalization is correct for every engine. `validatePolygonRing` is idempotent
 * — validating an already-canonical ring returns it unchanged.
 *
 * v1 REJECTS antimeridian-crossing polygons (a correct geodesic split needs edge-level great-circle
 * handling and is NOT the same as the bbox split); the caller surfaces the rejection.
 */

export const MAX_POLYGON_VERTICES = 200;

export type PolygonRing = [number, number][]; // [lng, lat] pairs, OPEN (first != last)

export type RingRejectReason =
  | 'too-few-vertices'
  | 'non-finite'
  | 'lat-out-of-range'
  | 'lng-out-of-range'
  | 'antimeridian'
  | 'degenerate'
  | 'self-intersecting'
  | 'too-many-vertices';

export type RingValidation =
  | { ok: true; ring: PolygonRing }
  | { ok: false; reason: RingRejectReason };

/**
 * Canonical OPEN ring: strip a trailing cyclic-close vertex (`first === last`), then drop
 * consecutive duplicate vertices. Idempotent. Longitudes are NOT wrapped here (validate owns that).
 */
export function normalizePolygonRing(pts: readonly [number, number][]): PolygonRing {
  const src = pts.slice();
  if (src.length >= 2) {
    const a = src[0];
    const b = src[src.length - 1];
    if (a[0] === b[0] && a[1] === b[1]) src.pop(); // cyclic close → open
  }
  const out: PolygonRing = [];
  for (const p of src) {
    const prev = out[out.length - 1];
    if (!prev || prev[0] !== p[0] || prev[1] !== p[1]) out.push([p[0], p[1]]);
  }
  return out;
}

/** Twice the signed area of the closed ring (shoelace). > 0 ⇒ counter-clockwise (lng=x, lat=y up). */
function signedArea2(ring: readonly [number, number][]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    sum += x1 * y2 - x2 * y1;
  }
  return sum;
}

/** Force counter-clockwise winding (reverse a clockwise ring). No-op when already CCW. */
export function ensureCcw(ring: PolygonRing): PolygonRing {
  return signedArea2(ring) < 0 ? [...ring].reverse() : ring;
}

/** Orientation of ordered triplet: >0 CCW, <0 CW, 0 collinear. */
function cross(o: readonly number[], a: readonly number[], b: readonly number[]): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

function onSegment(p: readonly number[], q: readonly number[], r: readonly number[]): boolean {
  return (
    Math.min(p[0], r[0]) <= q[0] && q[0] <= Math.max(p[0], r[0]) &&
    Math.min(p[1], r[1]) <= q[1] && q[1] <= Math.max(p[1], r[1])
  );
}

/** True when segments p1p2 and p3p4 intersect (proper crossing OR a collinear-overlap touch). */
function segmentsIntersect(
  p1: readonly number[], p2: readonly number[], p3: readonly number[], p4: readonly number[],
): boolean {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  if (d1 === 0 && onSegment(p3, p1, p4)) return true;
  if (d2 === 0 && onSegment(p3, p2, p4)) return true;
  if (d3 === 0 && onSegment(p1, p3, p2)) return true;
  if (d4 === 0 && onSegment(p1, p4, p2)) return true;
  return false;
}

/**
 * True when any pair of NON-ADJACENT edges of the closed ring intersect — including the implicit
 * closing edge (last→first). Adjacent edges legitimately share an endpoint, so they are skipped.
 */
export function ringSelfIntersects(ring: PolygonRing): boolean {
  const n = ring.length;
  if (n < 4) return false; // a triangle's 3 edges are all pairwise adjacent
  for (let i = 0; i < n; i++) {
    const a1 = ring[i];
    const a2 = ring[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // Skip edges sharing a vertex: consecutive (j===i+1) and the first/last wrap (i===0 && j===n-1).
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsIntersect(a1, a2, ring[j], ring[(j + 1) % n])) return true;
    }
  }
  return false;
}

/**
 * Validate + canonicalize a drawn/persisted ring. Returns a CCW open ring on success, or a tagged
 * rejection. Used by BOTH the draw handler (on close) and the session coercer (on load) so a
 * hand-edited/corrupt persisted ring can never reach SQL emit.
 */
export function validatePolygonRing(pts: readonly [number, number][]): RingValidation {
  for (const p of pts) {
    if (!Array.isArray(p) || p.length < 2 || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) {
      return { ok: false, reason: 'non-finite' };
    }
  }
  // Longitudes must already be in [-180,180] — the draw handler applies MapLibre's `LngLat.wrap()`
  // to world-copy clicks before building the ring, so a value outside the range here is garbage.
  for (const [lng, lat] of pts) {
    if (lat < -90 || lat > 90) return { ok: false, reason: 'lat-out-of-range' };
    if (lng < -180 || lng > 180) return { ok: false, reason: 'lng-out-of-range' };
  }
  const ring = normalizePolygonRing(pts);
  if (ring.length < 3) return { ok: false, reason: 'too-few-vertices' };
  if (ring.length > MAX_POLYGON_VERTICES) return { ok: false, reason: 'too-many-vertices' };
  // Antimeridian: after wrapping, a legitimate (non-crossing) ring spans < 180° of longitude; any
  // edge whose endpoints differ by > 180° is a wrap the emit would mis-close (v1 rejects, no split).
  for (let i = 0; i < ring.length; i++) {
    const next = ring[(i + 1) % ring.length];
    if (Math.abs(ring[i][0] - next[0]) > 180) return { ok: false, reason: 'antimeridian' };
  }
  if (signedArea2(ring) === 0) return { ok: false, reason: 'degenerate' }; // collinear / zero-area
  const ccw = ensureCcw(ring);
  if (ringSelfIntersects(ccw)) return { ok: false, reason: 'self-intersecting' };
  return { ok: true, ring: ccw };
}
